// Cookie-consent model, shared by the server (root layout) and the browser.
//
// The decision lives in a first-party cookie rather than localStorage on
// purpose. The previous consent banner (#3473, removed in #3499) kept its
// choice in localStorage, which the server cannot read; its `useSyncExternalStore`
// server snapshot was therefore always `null`, so the *full banner* was
// rendered into the SSR HTML of every page and only collapsed after hydration.
// That is what made the banner appear to pop on every page.
//
// A cookie is readable during the server render, so a browser that has already
// decided ships zero banner markup and zero third-party tags. It also means the
// cookie MUST be written with an explicit `path=/` — without it the browser
// scopes the cookie to the writing page's directory, and the banner reappears
// on every other path.

export const CONSENT_COOKIE_NAME = 'soku_consent'
export const CONSENT_VERSION = 1

/** One year, the conventional consent lifetime before re-asking. */
export const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

export type ConsentCategories = {
  /** Product analytics — PostHog. */
  analytics: boolean
  /** Advertising / attribution — Meta, Google, Gravity, Rewardful. */
  marketing: boolean
}

export type ConsentDecision = ConsentCategories & {
  version: number
  /** Epoch seconds, for audit and for re-asking when the policy changes. */
  decidedAt: number
}

export const CONSENT_ALL: ConsentCategories = {
  analytics: true,
  marketing: true,
}

export const CONSENT_NONE: ConsentCategories = {
  analytics: false,
  marketing: false,
}

/**
 * Wire format: `<version>.<analytics>.<marketing>.<decidedAt>` — e.g.
 * `1.1.0.1785900000`. Compact, cookie-safe without escaping, and trivially
 * parseable on both sides.
 */
export function serializeConsent(
  categories: ConsentCategories,
  decidedAt: number,
): string {
  return [
    CONSENT_VERSION,
    categories.analytics ? 1 : 0,
    categories.marketing ? 1 : 0,
    Math.floor(decidedAt),
  ].join('.')
}

/**
 * Parse a stored decision. Returns null for anything unrecognized — a missing,
 * malformed, or older-version cookie all mean "not decided yet", so the banner
 * shows again rather than silently assuming consent.
 */
export function parseConsent(
  raw: string | undefined | null,
): ConsentDecision | null {
  if (!raw) return null
  const parts = raw.split('.')
  if (parts.length !== 4) return null

  const [rawVersion, rawAnalytics, rawMarketing, rawDecidedAt] = parts
  const version = Number(rawVersion)
  const decidedAt = Number(rawDecidedAt)
  if (version !== CONSENT_VERSION) return null
  if (!Number.isFinite(decidedAt) || decidedAt <= 0) return null
  if (rawAnalytics !== '0' && rawAnalytics !== '1') return null
  if (rawMarketing !== '0' && rawMarketing !== '1') return null

  return {
    version,
    analytics: rawAnalytics === '1',
    marketing: rawMarketing === '1',
    decidedAt,
  }
}

/** Analytics SDKs may only load once the user has actively allowed them. */
export function allowsAnalytics(decision: ConsentDecision | null): boolean {
  return decision?.analytics === true
}

/** Advertising / attribution tags may only load once actively allowed. */
export function allowsMarketing(decision: ConsentDecision | null): boolean {
  return decision?.marketing === true
}
